import { Tag } from 'antd';
import dayjs from 'dayjs';

/** Share transfer OCR dates are DD-MM-YYYY (e.g. 08-05-2026 = 8 May 2026). */
export const parseShareTransferDate = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  const ddMmYyyy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (ddMmYyyy) {
    const day = parseInt(ddMmYyyy[1], 10);
    const month = parseInt(ddMmYyyy[2], 10) - 1;
    const year = parseInt(ddMmYyyy[3], 10);
    const parsed = dayjs(new Date(Date.UTC(year, month, day)));
    return parsed.isValid() ? parsed : null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

export const MATCH_STATE_LABEL = {
  ready: 'Ready to transfer',
  blocked: 'Blocked',
  unmatched: 'Unmatched',
  ambiguous: 'Ambiguous',
  already_transferred: 'Already transferred',
  already_transferred_manual: 'Already transferred manually',
  already_transferred_ambiguous: 'Already transferred (ambiguous)',
};

export const MATCH_STATE_COLOR = {
  ready: 'green',
  blocked: 'orange',
  unmatched: 'default',
  ambiguous: 'red',
  already_transferred: 'blue',
  already_transferred_manual: 'cyan',
  already_transferred_ambiguous: 'default',
};

export const formatReconcileDate = (value) => {
  const parsed = parseShareTransferDate(value);
  return parsed ? parsed.format('DD MMM YYYY') : '—';
};

export function getReadyMatchColumns() {
  return [
    {
      title: 'Investor',
      key: 'investor',
      render: (_, row) => row.order?.investor_name,
    },
    {
      title: 'ISIN',
      key: 'isin',
      render: (_, row) => row.extraction?.isin,
    },
    {
      title: 'PDF Date',
      key: 'pdf_date',
      render: (_, row) => formatReconcileDate(row.extraction?.date),
    },
    {
      title: 'Txn Ref',
      key: 'transaction_ref',
      width: 120,
      render: (_, row) => row.extraction?.transaction_ref || '—',
    },
    {
      title: 'Qty',
      key: 'qty',
      width: 80,
      render: (_, row) => row.extraction?.qty,
    },
    {
      title: 'Demat',
      key: 'demat',
      render: (_, row) => row.extraction?.demat_number,
    },
    {
      title: 'Order #',
      key: 'order_number',
      render: (_, row) => row.order?.order_number,
    },
    {
      title: 'Payment confirmed',
      key: 'payment_confirmed_at',
      render: (_, row) => formatReconcileDate(row.order?.payment_confirmed_at),
    },
    {
      title: 'Product',
      key: 'product',
      render: (_, row) => row.order?.product_name,
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, row) => (
        <Tag color={MATCH_STATE_COLOR[row.match_state]}>
          {MATCH_STATE_LABEL[row.match_state]}
        </Tag>
      ),
    },
  ];
}

export function getBlockedMatchColumns() {
  return [
    ...getReadyMatchColumns().filter((c) => c.key !== 'status'),
    {
      title: 'Reason',
      key: 'reason',
      render: (_, row) => row.blocker_reason || '—',
    },
  ];
}

export function getUnmatchedExtractionColumns() {
  return [
    { title: 'ISIN', key: 'isin', render: (_, row) => row.extraction?.isin },
    {
      title: 'PDF Date',
      key: 'pdf_date',
      render: (_, row) => formatReconcileDate(row.extraction?.date),
    },
    { title: 'Qty', key: 'qty', render: (_, row) => row.extraction?.qty },
    {
      title: 'Demat',
      key: 'demat',
      render: (_, row) => row.extraction?.demat_number,
    },
    {
      title: 'Why no match',
      key: 'debug',
      render: (_, row) => {
        const candidate = row.debug_candidates?.[0];
        if (!candidate?.failure_reasons?.length) return '—';
        return candidate.failure_reasons.join('; ');
      },
    },
  ];
}

export function getUnmatchedOrderColumns() {
  return [
    { title: 'Order #', dataIndex: 'order_number', key: 'order_number' },
    { title: 'Investor', dataIndex: 'investor_name', key: 'investor_name' },
    { title: 'Product', dataIndex: 'product_name', key: 'product_name' },
    {
      title: 'ISIN(s)',
      key: 'isins',
      render: (_, row) => (row.product_isins || []).join(', ') || '—',
    },
    { title: 'Qty', dataIndex: 'remaining_qty', key: 'remaining_qty', width: 80 },
    {
      title: 'Demat',
      dataIndex: 'order_demat_number',
      key: 'order_demat_number',
    },
    {
      title: 'Payment confirmed',
      dataIndex: 'payment_confirmed_at',
      key: 'payment_confirmed_at',
      render: (value) => formatReconcileDate(value),
    },
  ];
}

export function getAlreadyTransferredColumns() {
  return [
    {
      title: 'Investor',
      key: 'investor',
      render: (_, row) => row.order?.investor_name,
    },
    {
      title: 'ISIN',
      key: 'isin',
      render: (_, row) => row.extraction?.isin,
    },
    {
      title: 'PDF Date',
      key: 'pdf_date',
      render: (_, row) => formatReconcileDate(row.extraction?.date),
    },
    {
      title: 'Txn Ref',
      key: 'transaction_ref',
      width: 120,
      render: (_, row) => row.extraction?.transaction_ref || '—',
    },
    {
      title: 'Qty',
      key: 'qty',
      width: 80,
      render: (_, row) => row.extraction?.qty,
    },
    {
      title: 'Demat',
      key: 'demat',
      render: (_, row) => row.extraction?.demat_number,
    },
    {
      title: 'Order #',
      key: 'order_number',
      render: (_, row) => row.order?.order_number,
    },
    {
      title: 'Transferred on',
      key: 'completed_at',
      render: (_, row) => formatReconcileDate(row.order?.completed_at),
    },
    {
      title: 'Status',
      key: 'status',
      width: 160,
      render: (_, row) => (
        <Tag color={MATCH_STATE_COLOR[row.match_state]}>
          {MATCH_STATE_LABEL[row.match_state]}
        </Tag>
      ),
    },
  ];
}

export function buildBulkTransferPayload(selectedRows) {
  return selectedRows.map((row) => {
    const parsed = parseShareTransferDate(row.extraction?.date);
    return {
      order_uuid: row.order.uuid,
      transfer_date: parsed
        ? parsed.startOf('day').toISOString()
        : dayjs().startOf('day').toISOString(),
      remarks: 'Bulk share transfer reconciliation',
      transaction_ref: row.extraction?.transaction_ref ?? null,
    };
  });
}

export function rowKeyForMatch(row) {
  return `${row.extraction?.row_index}-${row.order?.uuid || 'none'}`;
}

const fingerprintKey = (isin, demat, qty) =>
  [isin, demat, String(qty)].filter(Boolean).join('|');

/** Same ISIN+demat+qty in Already transferred and Unmatched orders (repeat-order signal). */
export function hasDuplicateFingerprintOverlap(alreadyTransferredRows, unmatchedOrders) {
  const transferredKeys = new Set(
    (alreadyTransferredRows || []).map((row) =>
      fingerprintKey(row.extraction?.isin, row.extraction?.demat_number, row.extraction?.qty)
    )
  );
  return (unmatchedOrders || []).some((order) =>
    (order.product_isins || []).some((isin) =>
      transferredKeys.has(
        fingerprintKey(isin, order.order_demat_number, order.remaining_qty)
      )
    )
  );
}
